import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import PublicToolsMenu from '../components/public/PublicToolsMenu';
import { AskAvailabilityButton, SavePhotoButton, resolveImage } from '../components/public/CatalogHelpers';
import { ProductImageCarousel, ProductInfoButton } from '../components/public/ProductMediaCard';
import { useTrackPage } from '../utils/sellerTracking';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CODE_KEY = 'catalog_code';

const SCOPE_BADGES = {
  local:    { icon: '🏪', label: 'Solo tienda',    className: 'bg-purple-100 text-purple-700 border-purple-200' },
  shipment: { icon: '🚚', label: 'Solo envío',     className: 'bg-blue-100 text-blue-700 border-blue-200' },
  both:     { icon: '✅', label: 'Tienda y envío', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

function DaysLeftBadge({ days }) {
  if (days === null || days === undefined) return null;
  if (days < 0) return null;
  let cls = 'bg-emerald-100 text-emerald-700';
  let label = `${days} días restantes`;
  if (days === 0) { cls = 'bg-red-100 text-red-700'; label = '¡Último día!'; }
  else if (days === 1) { cls = 'bg-red-100 text-red-700'; label = '¡Mañana termina!'; }
  else if (days <= 3) { cls = 'bg-orange-100 text-orange-700'; label = `¡${days} días!`; }
  else if (days <= 7) { cls = 'bg-amber-100 text-amber-700'; label = `${days} días restantes`; }
  return <span className={`text-[11px] font-semibold rounded-full px-2.5 py-1 ${cls}`}>⏳ {label}</span>;
}

function CatalogCard({ item, seller, priority = false }) {
  const priceLabel = item.price_label; // 'no_individual' | 'coming_soon' | null
  const hasPromo = !priceLabel && item.has_active_promo && item.promo_price > 0;
  const displayPrice = hasPromo ? item.promo_price : item.base_price;
  const discount = hasPromo && item.base_price > item.promo_price
    ? Math.round((1 - item.promo_price / item.base_price) * 100)
    : 0;
  const scope = SCOPE_BADGES[item.scope] || SCOPE_BADGES.both;

  return (
    <article className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col hover:shadow-md transition" data-testid={`catalog-card-${item.id}`}>
      <ProductImageCarousel
        mainImage={item.image_url}
        extraImages={item.extra_images}
        alt={item.name}
        fallbackEmoji="📦"
        priority={priority}
      >
        {discount > 0 && (
          <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold rounded-full px-2 py-1 shadow-md pointer-events-none z-10">
            -{discount}%
          </div>
        )}
        {item.is_new_arrival && (
          <div className="absolute left-2 bg-emerald-500 text-white text-[10px] font-black rounded-full px-2.5 py-1 shadow-md tracking-wider pointer-events-none z-10" style={{ top: discount > 0 ? '2.25rem' : '0.5rem' }}>
            🆕 NUEVO
          </div>
        )}
        <ProductInfoButton productName={item.name} info={item.product_info} sku={item.sku} purchaseCost={item.purchase_cost} stockM={item.stock_m} stockA={item.stock_a} commission={item.default_commission} wholesale={item.mayoreo} />
      </ProductImageCarousel>

      <div className="p-3 flex-1 flex flex-col gap-2">
        <h3 className="font-bold text-slate-900 leading-tight break-words">{item.name}</h3>

        <div className="flex items-baseline gap-2 flex-wrap">
          {priceLabel === 'no_individual' ? (
            <span
              className="text-base font-bold text-slate-600 bg-slate-100 border border-slate-200 rounded-lg px-2.5 py-1"
              data-testid={`catalog-price-label-no-individual-${item.id}`}
            >
              🧩 No para venta individual
            </span>
          ) : priceLabel === 'coming_soon' ? (
            <span
              className="text-base font-bold text-amber-800 bg-amber-100 border border-amber-200 rounded-lg px-2.5 py-1"
              data-testid={`catalog-price-label-coming-soon-${item.id}`}
            >
              ⏳ Próximamente
            </span>
          ) : (
            <>
              <span className={`text-2xl font-bold ${hasPromo ? 'text-red-700' : 'text-slate-900'}`}>
                ${displayPrice.toFixed(2)}
              </span>
              {hasPromo && (
                <span className="text-sm text-slate-400 line-through">${item.base_price.toFixed(2)}</span>
              )}
            </>
          )}
        </div>

        {hasPromo && item.promo_type === 'min_qty' && item.promo_min_qty > 0 && (
          <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-md px-2 py-1">
            🔢 Comprando <b>{item.promo_min_qty} o más</b>
          </p>
        )}

        {hasPromo && (
          <div className="flex flex-wrap gap-1.5">
            <span className={`inline-flex items-center gap-1 text-[11px] font-medium border rounded-full px-2 py-0.5 ${scope.className}`}>
              <span>{scope.icon}</span> {scope.label}
            </span>
            <DaysLeftBadge days={item.days_left} />
          </div>
        )}

        <div className="grid grid-cols-1 gap-1.5 mt-auto pt-2">
          <AskAvailabilityButton
            productName={item.name}
            price={priceLabel ? 0 : displayPrice}
            sellerPhone={seller?.phone}
            sellerName={seller?.name}
            sellerSlug={seller?.catalog_slug || seller?.id}
          />
          <SavePhotoButton imageUrl={item.image_url} filename={item.name} />
        </div>
      </div>
    </article>
  );
}

function CodeGate() {
  // Componente conservado por compatibilidad — actualmente no se usa porque
  // el catálogo es público. Se deja para reintroducir el gate rápidamente si
  // en el futuro se necesita.
  return null;
}

// Normaliza un teléfono al formato internacional para WhatsApp (asume MX si
// son 10 dígitos y no viene con "+").
function _normalizePhoneForWa(phone) {
  const clean = String(phone || '').replace(/[^\d+]/g, '');
  if (!clean) return '';
  if (clean.startsWith('+')) return clean.slice(1);
  if (clean.length === 10) return `52${clean}`;
  return clean;
}

// Banner del vendedor: nombre + botón WhatsApp
function SellerBanner({ seller, currentUrl }) {
  if (!seller) return null;
  const waPhone = _normalizePhoneForWa(seller.phone);
  const waUrl = waPhone
    ? `https://wa.me/${waPhone}?text=${encodeURIComponent('¡Hola! Vi tu catálogo, me interesa un producto.')}`
    : null;

  const trackWaClick = () => {
    const slug = seller.catalog_slug || seller.id;
    if (!slug) return;
    axios.post(`${API}/public/catalog-track`, { slug, event: 'wa_click' }).catch(() => {});
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      alert('✅ Enlace copiado');
    } catch { /* ignore */ }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 -mt-3 mb-4" data-testid="catalog-seller-banner">
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white font-black text-xl flex items-center justify-center flex-shrink-0 uppercase">
          {(seller.name || '?').charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Tu vendedor</p>
          <p className="text-lg font-bold text-slate-900 truncate" data-testid="catalog-seller-name">{seller.name}</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {waUrl && (
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackWaClick}
              className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full px-4 py-2 text-sm shadow transition"
              data-testid="catalog-seller-whatsapp"
            >
              <span className="text-base leading-none">💬</span>
              <span className="hidden sm:inline">WhatsApp</span>
            </a>
          )}
          <button
            onClick={copyLink}
            className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-full px-3 py-2 text-sm transition"
            data-testid="catalog-seller-copy-link"
            aria-label="Copiar enlace"
            title="Copiar enlace"
          >🔗</button>
        </div>
      </div>
    </div>
  );
}

export default function PublicCatalog() {
  const [items, setItems] = useState(null);
  const [categories, setCategories] = useState([]);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'promo'
  const [selectedCats, setSelectedCats] = useState(() => new Set());
  const [catSheetOpen, setCatSheetOpen] = useState(false);

  // Leer ?v=<seller_id> de la URL (persiste en la barra al compartir)
  const sellerId = useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return (p.get('v') || '').trim() || null;
    } catch { return null; }
  }, []);

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Trackear tiempo en el catálogo (solo si el dispositivo ya está reclamado
  // por un vendedor — vía NIP login previo o link personalizado).
  useTrackPage('catalog_view');

  // Cargar catálogo (público, sin código) y opcionalmente info del vendedor
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const [catRes, sellerRes] = await Promise.all([
          axios.get(`${API}/public/catalog`),
          sellerId ? axios.get(`${API}/public/seller-info/${sellerId}`).catch(() => null) : Promise.resolve(null),
        ]);
        if (cancel) return;
        setItems(catRes.data?.items || []);
        setCategories(catRes.data?.categories || []);
        if (sellerRes && sellerRes.data) setSeller(sellerRes.data);
      } catch (e) {
        if (!cancel) setError('No se pudo cargar el catálogo');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [sellerId]);

  // Trackear visita al catálogo (una vez por carga, con debounce por localStorage
  // para evitar contar reloads dentro del mismo minuto).
  useEffect(() => {
    if (!sellerId) return;
    try {
      const key = `catalog_visit_${sellerId}`;
      const last = Number(localStorage.getItem(key) || 0);
      const now = Date.now();
      if (now - last < 60_000) return; // dedupe 60s
      localStorage.setItem(key, String(now));
    } catch { /* ignore */ }
    axios.post(`${API}/public/catalog-track`, { slug: sellerId, event: 'visit' }).catch(() => {});
  }, [sellerId]);

  // Cerrar sheet con ESC
  useEffect(() => {
    if (!catSheetOpen) return;
    const h = (e) => { if (e.key === 'Escape') setCatSheetOpen(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [catSheetOpen]);

  // Solo categorías padre (raíz) — las subcategorías se agrupan bajo su padre.
  // Al seleccionar una categoría padre, se incluyen los productos de sus subcategorías.
  const catsInUse = useMemo(() => {
    if (!items) return [];
    const usedIds = new Set(items.map(it => it.category_id).filter(Boolean));
    // Mapa parent_id → hijos usados (para "arrastrar" al padre si sólo hay uso en subcategorías)
    const rootCats = categories.filter(c => !c.parent_id);
    return rootCats.filter(root => {
      // Incluir el padre si él o cualquier subcategoría suya tiene productos
      if (usedIds.has(root.id)) return true;
      return categories.some(c => c.parent_id === root.id && usedIds.has(c.id));
    });
  }, [items, categories]);

  // ¿Hay productos sin categoría? Se ofrece como filtro adicional.
  const uncategorizedCount = useMemo(() => {
    if (!items) return 0;
    return items.filter(it => !it.category_id).length;
  }, [items]);

  // ID sintético para representar "Sin categoría" en el Set de selección.
  const UNCAT_ID = '__uncategorized__';

  // Set expandido: por cada categoría raíz seleccionada, incluir su id y las de sus subcategorías.
  const expandedCatFilter = useMemo(() => {
    if (selectedCats.size === 0) return null;
    const expanded = new Set();
    selectedCats.forEach(rootId => {
      expanded.add(rootId);
      categories.forEach(c => {
        if (c.parent_id === rootId) expanded.add(c.id);
      });
    });
    return expanded;
  }, [selectedCats, categories]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    let list = items;
    if (filter === 'promo') list = list.filter((i) => i.has_active_promo);
    if (expandedCatFilter) {
      const includeUncat = selectedCats.has(UNCAT_ID);
      list = list.filter((i) => {
        if (!i.category_id) return includeUncat;
        return expandedCatFilter.has(i.category_id);
      });
    }
    if (q) list = list.filter((i) => (i.name || '').toLowerCase().includes(q));
    return list;
  }, [items, query, filter, expandedCatFilter, selectedCats]);

  const toggleCat = (id) => {
    setSelectedCats(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const promoCount = items?.filter((i) => i.has_active_promo).length || 0;
  const activeCatsCount = selectedCats.size;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PublicToolsMenu current="/catalogo" />

      <header
        className="bg-gradient-to-br from-slate-800 to-slate-900 text-white shadow"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)' }}
      >
        <div className="max-w-5xl mx-auto px-4 pb-8">
          <div className="text-center">
            <p className="text-[11px] uppercase tracking-widest opacity-80">Catálogo</p>
            <h1 className="text-2xl sm:text-3xl font-bold mt-1">📦 Productos disponibles</h1>
            <p className="text-xs opacity-80 mt-1">
              {loading ? 'Cargando…' : `${items?.length || 0} productos${promoCount > 0 ? ` · ${promoCount} en oferta` : ''}`}
            </p>
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <input
              type="search"
              placeholder="🔍 Buscar por nombre…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 bg-white/95 text-slate-900 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-500"
              data-testid="catalog-search"
            />
            <div className="flex gap-1 rounded-lg bg-white/10 p-1 text-xs">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1.5 rounded-md font-medium ${filter === 'all' ? 'bg-white text-slate-800' : 'text-white'}`}
                data-testid="catalog-filter-all"
              >Todos</button>
              <button
                onClick={() => {
                  // Al ver Ofertas, limpiar categorías para no ocultar promos que
                  // están fuera de la categoría seleccionada.
                  setSelectedCats(new Set());
                  setFilter('promo');
                  try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* ignore */ }
                }}
                className={`px-3 py-1.5 rounded-md font-medium ${filter === 'promo' ? 'bg-white text-slate-800' : 'text-white'}`}
                data-testid="catalog-filter-promo"
              >🏷️ Ofertas ({promoCount})</button>
            </div>
          </div>
        </div>
      </header>

      <SellerBanner seller={seller} currentUrl={currentUrl} />

      <main className="max-w-5xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm mb-4" data-testid="catalog-error">{error}</div>
        )}

        {loading && !items && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="aspect-square bg-slate-100 animate-pulse" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-slate-100 rounded animate-pulse" />
                  <div className="h-6 bg-slate-100 rounded animate-pulse w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {items && filtered.length === 0 && (
          <div className="text-center py-16" data-testid="catalog-empty">
            <div className="text-5xl mb-2">🔍</div>
            <p className="text-slate-500">Sin resultados para tu búsqueda.</p>
          </div>
        )}

        {items && filtered.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="catalog-grid">
            {filtered.map((it, i) => (
              <CatalogCard
                key={`${it.kind}-${it.id}`}
                item={it}
                seller={seller}
                priority={i < 8}
              />
            ))}
          </div>
        )}
      </main>

      {/* Floating "Categorías" button (esquina inferior derecha, sobre safe-area) */}
      {catsInUse.length > 0 && (
        <button
          onClick={() => setCatSheetOpen(true)}
          data-testid="catalog-open-cats-btn"
          aria-label="Filtrar por categorías"
          className="fixed z-40 bottom-5 right-5 sm:bottom-6 sm:right-6 bg-red-600 hover:bg-red-700 active:scale-95 text-white shadow-2xl rounded-full pl-4 pr-5 py-3 flex items-center gap-2 font-semibold text-sm transition"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.25rem)' }}
        >
          <span className="text-lg leading-none">🗂️</span>
          <span>Categorías</span>
          {activeCatsCount > 0 && (
            <span
              className="ml-1 bg-white text-red-700 rounded-full text-[11px] font-bold min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center"
              data-testid="catalog-cats-badge"
            >{activeCatsCount}</span>
          )}
        </button>
      )}

      {/* Bottom sheet / dialog para seleccionar categorías */}
      {catSheetOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setCatSheetOpen(false)}
          data-testid="catalog-cats-sheet"
        >
          <div
            className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Filtrar por categorías"
          >
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h2 className="font-bold text-slate-900">🗂️ Filtrar por categorías</h2>
                <p className="text-xs text-slate-500">
                  {activeCatsCount === 0
                    ? 'Mostrando todas las categorías'
                    : `${activeCatsCount} categoría${activeCatsCount === 1 ? '' : 's'} seleccionada${activeCatsCount === 1 ? '' : 's'}`}
                </p>
              </div>
              <button
                onClick={() => setCatSheetOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"
                aria-label="Cerrar"
                data-testid="catalog-cats-close"
              >✕</button>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              <div className="grid grid-cols-1 gap-1.5">
                {catsInUse.map((c) => {
                  const active = selectedCats.has(c.id);
                  // Contar productos del padre + todas sus subcategorías
                  const childIds = new Set(categories.filter(x => x.parent_id === c.id).map(x => x.id));
                  const count = items.filter(it =>
                    it.category_id === c.id || (it.category_id && childIds.has(it.category_id))
                  ).length;
                  return (
                    <button
                      key={c.id}
                      onClick={() => toggleCat(c.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 transition text-left ${
                        active
                          ? 'border-red-500 bg-red-50 text-red-900'
                          : 'border-slate-200 hover:border-slate-300 bg-white text-slate-800'
                      }`}
                      data-testid={`catalog-cat-toggle-${c.id}`}
                    >
                      <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                        active ? 'border-red-500 bg-red-500 text-white' : 'border-slate-300 bg-white'
                      }`}>
                        {active && <span className="text-xs font-black leading-none">✓</span>}
                      </span>
                      <span className="flex-1 font-medium truncate">{c.name}</span>
                      <span className="text-xs text-slate-500 tabular-nums">{count}</span>
                    </button>
                  );
                })}
                {uncategorizedCount > 0 && (() => {
                  const active = selectedCats.has(UNCAT_ID);
                  return (
                    <button
                      key={UNCAT_ID}
                      onClick={() => toggleCat(UNCAT_ID)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 border-dashed transition text-left ${
                        active
                          ? 'border-red-500 bg-red-50 text-red-900'
                          : 'border-slate-300 hover:border-slate-400 bg-slate-50 text-slate-700'
                      }`}
                      data-testid="catalog-cat-toggle-uncategorized"
                    >
                      <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                        active ? 'border-red-500 bg-red-500 text-white' : 'border-slate-300 bg-white'
                      }`}>
                        {active && <span className="text-xs font-black leading-none">✓</span>}
                      </span>
                      <span className="flex-1 font-medium truncate italic">📂 Sin categoría</span>
                      <span className="text-xs text-slate-500 tabular-nums">{uncategorizedCount}</span>
                    </button>
                  );
                })()}
              </div>
              {catsInUse.length === 0 && (
                <p className="text-center text-sm text-slate-500 py-8">Aún no hay categorías con productos.</p>
              )}
            </div>

            <div className="p-3 border-t grid grid-cols-2 gap-2">
              <button
                onClick={() => setSelectedCats(new Set())}
                disabled={activeCatsCount === 0}
                className="py-2.5 rounded-xl font-semibold text-sm text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                data-testid="catalog-cats-clear"
              >Limpiar</button>
              <button
                onClick={() => {
                  setCatSheetOpen(false);
                  try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* ignore */ }
                }}
                className="py-2.5 rounded-xl font-semibold text-sm text-white bg-red-600 hover:bg-red-700 transition"
                data-testid="catalog-cats-apply"
              >Ver productos</button>
            </div>
          </div>
        </div>
      )}

      <footer className="text-center text-[11px] text-slate-400 py-6">
        Sistema POS · Catálogo público
      </footer>
    </div>
  );
}
